import { getLoketLeaveBalance } from '../../../helpers/api-ts/leaveBalance'
import { getLeaveOfAbsences } from '../../../helpers/api-ts/leaveOfAbsence'
import { getEndOfWorkDay, getStartOfWorkDay } from '../../../helpers/date'
import { LeaveType } from '../../../models'
import { AppFilter } from '../../../redux/filter-ts/filterModels'
import { setPagination } from '../../../redux/pagination/paginationActions'
import getHoursFromLeaveOfAbsences from '../../../services/LeaveOfAbsenceHoursService/getHoursFromLeaveOfAbsences'
import { Department } from '../../entities/Department/Department'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { getHourBalancesApiCall } from '../../entities/HourBalances/HourBalancesService'
import { roundNumberDecimals, roundNumberToRoundedNumberToQuarter } from '../../helpers/math'
import { TimeModeType } from '../../types'
import { PaginationV3RequestApiParams } from '../../types/paginations'
import { TypedDispatch } from '../store'
import { setError, setHourBalances, setIsLoading } from './hourBalancesReducer'

export const getHourBalances = (
    startDate: Date,
    endDate: Date,
    by: TimeModeType,
    filter: AppFilter,
    pagination: PaginationV3RequestApiParams,
    userDepartments: Department[],
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const result = await getHourBalancesApiCall({
            startDate,
            by,
            filter,
            pagination,
            userDepartments,
        })

        if (! isFetchResultSuccessful(result)) {
            dispatch(setError(result.error))
            return
        }

        const users = result.data
        const userIds = users.map(user => user.id)

        const [loketBalances, leaveOfAbsences] = await Promise.all([
            Promise.all(
                users
                    .filter(user => !! user.loketEmployeeId) // Filter out `null` and `0`
                    .map((user) => getLoketLeaveBalance(user.loketEmployeeId ?? '')),
            ),
            getLeaveOfAbsences({
                startDate: getStartOfWorkDay(startDate),
                endDate: getEndOfWorkDay(endDate),
                filter: { userIds },
                status: [1, 2],
                includes: ['leaveOfAbsenceHours', 'leaveOfAbsenceHours.payrollPeriod', 'user'],
            }),
        ])

        const data = users.map((user) => {
            const loketUser = loketBalances.find(
                loketBalance => loketBalance && loketBalance.loketEmployeeId === user.loketEmployeeId,
            )

            const userLeaveOfAbsences = leaveOfAbsences.filter(
                (absence) => absence.user && absence.user.id === user.id,
            )

            const usedTvtHours = getHoursFromLeaveOfAbsences(
                userLeaveOfAbsences,
                getStartOfWorkDay(startDate),
                getEndOfWorkDay(endDate),
                [LeaveType.tvt],
            )

            return {
                userId: user.id,
                userName: user.name,
                plannedHours: roundNumberDecimals(user.plannedHours || 0),
                contractHours: roundNumberToRoundedNumberToQuarter(user.contractHours),
                workedHours: roundNumberToRoundedNumberToQuarter(user.workedHours),
                absenceHours: roundNumberDecimals(user.absenceHours),
                leaveOfAbsenceHours: roundNumberDecimals(user.leaveHours),
                usedTvtHours: roundNumberDecimals(usedTvtHours),
                mutatedTvtHours: roundNumberToRoundedNumberToQuarter(user.tvtMutation),
                leaveOfAbsenceBalance: roundNumberToRoundedNumberToQuarter(loketUser?.leaveBalanceTotal || 0),
                tvtBalance: roundNumberToRoundedNumberToQuarter(loketUser?.timeForTimeTotal || 0),
            }
        })

        dispatch(setHourBalances(data))
        dispatch(setPagination(result.totalPages))
    } catch (error) {
        console.error('[getHourBalances]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}
